import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Plus, Trash2, Pencil, Download, Users as UsersIcon, Settings, Activity } from "lucide-react";
import api, { formatDetail, fmtINR } from "@/lib/api";
import { PayinConnectionTest } from "@/components/admin/PayinConnectionTest";
import { PayinHealthBar, timeAgo } from "@/components/admin/PayinHealthBar";
import { PayinHistory } from "@/components/payment/PayinHistory";
import { PartnerDefaults, emptyDefaults } from "@/components/admin/PartnerDefaults";
import { CallbackDomainField } from "@/components/admin/CallbackDomainField";
import { MidStatsCard, MidStatsStrip, RangeSwitch, useMidStats } from "@/components/payment/MidStats";

const Modal = ({ children }) => createPortal(
    <div className="fixed inset-0 bg-black/40 z-[100] flex items-start sm:items-center justify-center p-4 overflow-y-auto">
        {children}
    </div>, document.body);

/**
 * Admin Payin page — mirrors PayoutsAdminPage structure. Super admin only.
 * Lists wallets (all partners) + MID CRUD + assign MIDs/vendors to partners.
 */
export default function PayinAdminPage() {
    const [tab, setTab] = useState("wallets");
    return (
        <div className="p-6 space-y-5" data-testid="admin-payin-page">
            <div>
                <h1 className="text-xl font-bold">Payin (Admin)</h1>
                <p className="text-xs text-slate-500 mt-1">Collect payments on partners' behalf. Manage Payspher MIDs and assign them to partners.</p>
            </div>
            <div className="border-b border-slate-200 flex gap-6">
                {[["wallets", "Partner wallets"], ["mids", "MIDs"], ["stats", "MID Stats"], ["orders", "Orders"]].map(([k, l]) => (
                    <button key={k} data-testid={`payin-tab-${k}`} onClick={() => setTab(k)}
                            className={`py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-indigo-600 text-indigo-700 font-semibold" : "border-transparent text-slate-500"}`}>{l}</button>
                ))}
            </div>
            {tab === "wallets" && <WalletsTab />}
            {tab === "mids" && <MidsTab />}
            {tab === "stats" && <MidStatsCard path="/admin/payin/mids/stats" title="MID usage stats" testid="admin-mid-stats" />}
            {tab === "orders" && <OrdersTab />}
        </div>
    );
}

function WalletsTab() {
    const [wallets, setWallets] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [mids, setMids] = useState([]);
    const [err, setErr] = useState("");
    const [editing, setEditing] = useState(null);  // partner_id for assign modal
    const [vendorSel, setVendorSel] = useState([]);
    const [midSel, setMidSel] = useState([]);
    const [autoMin, setAutoMin] = useState(5);
    const [stuckMin, setStuckMin] = useState(60);
    const [defaults, setDefaults] = useState(emptyDefaults());
    const editingW = wallets.find((x) => x.partner_id === editing);
    const [hookOverride, setHookOverride] = useState(null);
    const [busy, setBusy] = useState(false);
    const [comEditing, setComEditing] = useState(null);  // partner_id for commissions
    const [comRows, setComRows] = useState([]);
    const [comBusy, setComBusy] = useState(false);

    const refresh = async () => {
        try {
            const [w, v, m] = await Promise.all([
                api.get("/admin/payin/wallets"),
                api.get("/admin/payin/vendors"),
                api.get("/admin/payin/mids"),
            ]);
            setWallets(w.data.items || []); setVendors(v.data.items || []); setMids(m.data.items || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { refresh(); }, []);

    const openEdit = (w) => {
        setEditing(w.partner_id);
        setVendorSel(w.allowed_payin_vendors || []);
        setMidSel(w.assigned_payin_mids || []);
        setAutoMin(w.auto_verify_minutes ?? 5);
        setStuckMin(w.stuck_alert_minutes ?? 60);
        setDefaults(emptyDefaults(w.payin_defaults));
        setHookOverride(null);
    };
    const openCommissions = async (w) => {
        setComEditing(w.partner_id); setErr(""); setComRows([]);
        try {
            const r = await api.get(`/admin/payin/wallet/${w.partner_id}/commissions`);
            setComRows(r.data.items || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    const saveCommission = async (mid_id, percent, flat_fee, active_from) => {
        setComBusy(true);
        try {
            const body = { percent: parseFloat(percent) || 0, flat_fee: parseFloat(flat_fee) || 0 };
            if (active_from) body.active_from = active_from;
            const r = await api.patch(`/admin/payin/wallet/${comEditing}/commissions/${mid_id}`, body);
            const fresh = r.data?.rate || {};
            setComRows((rows) => rows.map((row) => row.mid_id === mid_id
                ? { ...row, percent: fresh.percent ?? row.percent, flat_fee: fresh.flat_fee ?? row.flat_fee, scheduled: fresh.scheduled ?? null }
                : row));
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setComBusy(false); }
    };
    const cancelScheduled = async (mid_id) => {
        setComBusy(true);
        try {
            const r = await api.delete(`/admin/payin/wallet/${comEditing}/commissions/${mid_id}/scheduled`);
            const fresh = r.data?.rate || {};
            setComRows((rows) => rows.map((row) => row.mid_id === mid_id
                ? { ...row, scheduled: fresh.scheduled ?? null }
                : row));
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setComBusy(false); }
    };
    const save = async () => {
        setBusy(true); setErr("");
        try {
            await api.patch(`/admin/payin/wallet/${editing}/vendors`, { allowed_vendors: vendorSel });
            await api.patch(`/admin/payin/wallet/${editing}/mids`, { assigned_mids: midSel });
            await api.patch(`/admin/payin/wallet/${editing}/auto-verify`, { minutes: Math.max(0, parseInt(autoMin, 10) || 0) });
            await api.patch(`/admin/payin/wallet/${editing}/stuck-alert`, { minutes: Math.max(0, parseInt(stuckMin, 10) || 0) });
            await api.patch(`/admin/payin/wallet/${editing}/settings`, defaults);
            setEditing(null); refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <>
            {err && <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700 mb-3">{err}</div>}
            <FallbackDomainCard onChanged={refresh} />
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-3 border-b border-slate-100 text-sm font-semibold">Partner payin wallets ({wallets.length})</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-3 py-2 text-left">Partner</th>
                                <th className="px-3 py-2 text-left">Vendors</th>
                                <th className="px-3 py-2 text-left">MIDs</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {wallets.map((w) => (
                                <tr key={w.partner_id} className={`border-t border-slate-100 ${!w.wallet_initialised ? "bg-slate-50/50" : ""}`}
                                        data-testid={`payin-wallet-${w.partner_id}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-semibold">{w.business_name}
                                            {!w.wallet_initialised && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] bg-amber-100 text-amber-800">NOT SETUP</span>}
                                        </div>
                                        <div className="text-[10px] text-slate-500 font-mono">{w.partner_id}</div>
                                    </td>
                                    <td className="px-3 py-2">{(w.allowed_payin_vendors || []).join(", ") || <span className="text-slate-400 italic">none</span>}</td>
                                    <td className="px-3 py-2">
                                        {(w.assigned_payin_mids || []).length || <span className="text-slate-400 italic">none</span>}
                                        {(w.assigned_payin_mids || []).length > 0 && (
                                            <span data-testid={`payin-wallet-autoverify-${w.partner_id}`} title="Auto-verify interval for in-progress orders"
                                                  className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${w.auto_verify_minutes > 0 ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-500"}`}>
                                                {w.auto_verify_minutes > 0 ? `↻ every ${w.auto_verify_minutes} min` : "auto-verify off"}
                                            </span>
                                        )}
                                        {(w.assigned_payin_mids || []).length > 0 && (
                                            <span data-testid={`payin-wallet-stuck-${w.partner_id}`} title="Stuck-order alert threshold"
                                                  className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${w.stuck_alert_minutes > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                                                {w.stuck_alert_minutes > 0 ? `stuck > ${w.stuck_alert_minutes}m` : "stuck alert off"}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button onClick={() => openCommissions(w)} data-testid={`payin-com-${w.partner_id}`}
                                                className="text-emerald-600 hover:text-emerald-800 text-xs underline font-semibold mr-3">Commissions</button>
                                        <button onClick={() => openEdit(w)} data-testid={`payin-edit-${w.partner_id}`}
                                                className="text-indigo-600 hover:text-indigo-800 text-xs underline font-semibold">Assign</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <Modal>
                    <div className="bg-white rounded-xl w-full max-w-lg p-5 space-y-4" data-testid="payin-assign-modal">
                        <div className="flex items-center justify-between">
                            <h3 className="font-bold text-base">Assign payin — {editing}</h3>
                            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600">✕</button>
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Allowed vendors</label>
                            <div className="mt-2 space-y-1.5">
                                {vendors.map((v) => (
                                    <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="checkbox" checked={vendorSel.includes(v.id)}
                                                onChange={(e) => setVendorSel(e.target.checked ? [...vendorSel, v.id] : vendorSel.filter((x) => x !== v.id))} />
                                        {v.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Assigned MIDs (first active = default)</label>
                            <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
                                {mids.length === 0 && <div className="text-xs text-slate-400 italic">No payin MIDs created yet. Create one under the MIDs tab first.</div>}
                                {mids.map((m) => {
                                    const lockedElsewhere = m.dedicated_partner_id && m.dedicated_partner_id !== editing;
                                    return (
                                    <label key={m.mid_id} className={`flex items-center gap-2 text-sm ${lockedElsewhere ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                           title={lockedElsewhere ? `Dedicated to ${wallets.find((x) => x.partner_id === m.dedicated_partner_id)?.business_name || m.dedicated_partner_id}` : undefined}>
                                        <input type="checkbox" checked={midSel.includes(m.mid_id)} disabled={lockedElsewhere && !midSel.includes(m.mid_id)}
                                                data-testid={`payin-assign-mid-${m.mid_id}`}
                                                onChange={(e) => setMidSel(e.target.checked ? [...midSel, m.mid_id] : midSel.filter((x) => x !== m.mid_id))} />
                                        <span className="font-medium">{m.label}</span>
                                        {m.dedicated_partner_id && <span className="text-[10px]" title="Dedicated MID">🔒</span>}
                                        <span className="text-[10px] text-slate-500 font-mono">({m.vendor} · {m.mid_id})</span>
                                        <span className={`ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${m.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{m.status}</span>
                                    </label>
                                    );
                                })}
                            </div>
                        </div>
                        <MinutesField value={autoMin} onChange={setAutoMin} testid="payin-autoverify" label="Auto-verify in-progress orders"
                                      presets={[0, 2, 5, 10, 15, 30]} max={1440} summary={(n) => `Every ${n} min · last 24h`}
                                      help="Checks the vendor for every in-progress order from the last 24 hours at this interval, then updates status, commission and the partner callback." />
                        <MinutesField value={stuckMin} onChange={setStuckMin} testid="payin-stuck" label="Stuck order alert"
                                      presets={[0, 30, 60, 120, 240]} max={10080} summary={(n) => `Alert after ${n} min in progress`}
                                      help="Partner gets an in-app alert (bell + banner) when an order stays in progress longer than this." />
                        <PartnerDefaults value={defaults} onChange={setDefaults} defaultHook={hookOverride || editingW?.default_notification_url}
                                         partnerId={editing} callbackDomain={editingW?.callback_domain} onHookChange={setHookOverride} />
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-slate-200 text-sm hover:bg-slate-50">Cancel</button>
                            <button onClick={save} disabled={busy} data-testid="payin-assign-save"
                                    className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2">
                                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {comEditing && (
                <Modal>
                    <div className="bg-white rounded-xl w-full max-w-2xl p-5 space-y-4" data-testid="payin-commissions-modal">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-base">Commissions — {comEditing}</h3>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                    Charged on every successful PayIn capture. Rate changes only affect new captures.
                                </p>
                            </div>
                            <button onClick={() => setComEditing(null)} className="text-slate-400 hover:text-slate-600">✕</button>
                        </div>
                        {comRows.length === 0 ? (
                            <div className="text-xs text-slate-400 italic p-4 text-center">
                                No MIDs assigned to this partner. Use "Assign" first.
                            </div>
                        ) : (
                            <div className="border border-slate-200 rounded-md overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-600">
                                        <tr>
                                            <th className="px-3 py-2 text-left">MID</th>
                                            <th className="px-3 py-2 text-left">Vendor</th>
                                            <th className="px-3 py-2 text-right">Percent (%)</th>
                                            <th className="px-3 py-2 text-right">Flat ₹</th>
                                            <th className="px-3 py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {comRows.map((r) => (
                                            <CommissionRow key={r.mid_id} row={r} partnerId={comEditing} onSave={saveCommission} onCancelSched={cancelScheduled} busy={comBusy} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setComEditing(null)} data-testid="payin-com-close"
                                    className="btn-primary px-4 py-2 rounded-md text-sm font-semibold">Done</button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

function FallbackDomainCard({ onChanged }) {
    const [doc, setDoc] = useState(undefined);
    useEffect(() => { api.get("/admin/payin/callback-domain").then((r) => setDoc(r.data)).catch(() => setDoc(null)); }, []);
    if (doc === undefined) return null;
    return (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-3" data-testid="payin-fallback-domain-card">
            <CallbackDomainField value={doc?.domain ? doc : null} testid="payin-fallback-domain"
                                 label="Neutral fallback callback domain (used for partners without their own)"
                                 hint="Vendor-facing notification URLs use this host instead of your main domain once DNS is verified."
                                 setPath="/admin/payin/callback-domain" verifyPath="/admin/payin/callback-domain/verify" onUpdated={onChanged} />
        </div>
    );
}

function MinutesField({ value, onChange, testid, label, presets, max, summary, help }) {
    const n = parseInt(value, 10) || 0;
    const fmt = (m) => (m === 0 ? "Off" : m >= 60 && m % 60 === 0 ? `${m / 60}h` : `${m}m`);
    return (
        <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`${testid}-field`}>
            <div className="flex items-center justify-between gap-2">
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
                <span className="text-[11px] text-slate-500" data-testid={`${testid}-summary`}>{n > 0 ? summary(n) : "Off"}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
                {presets.map((m) => (
                    <button key={m} type="button" onClick={() => onChange(m)} data-testid={`${testid}-preset-${m}`}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${n === m ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        {fmt(m)}
                    </button>
                ))}
                <div className="flex items-center gap-1 ml-1">
                    <input type="number" min={0} max={max} value={value} onChange={(e) => onChange(e.target.value)}
                           data-testid={`${testid}-minutes`} className="input w-20 text-sm py-1" />
                    <span className="text-xs text-slate-500">min</span>
                </div>
            </div>
            <p className="text-[11px] text-slate-500">{help}</p>
        </div>
    );
}

function CommissionRow({ row, onSave, onCancelSched, busy, partnerId }) {
    const [pct, setPct] = useState(String(row.percent ?? 0));
    const [flat, setFlat] = useState(String(row.flat_fee ?? 0));
    const [scheduled, setScheduled] = useState(false);
    const [activeFrom, setActiveFrom] = useState("");
    const [saved, setSaved] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState(null);
    const dirty = parseFloat(pct) !== row.percent || parseFloat(flat) !== row.flat_fee || (scheduled && activeFrom);
    const save = async () => {
        let iso = null;
        if (scheduled && activeFrom) {
            // Convert local datetime-local to UTC ISO
            iso = new Date(activeFrom).toISOString();
        }
        await onSave(row.mid_id, pct, flat, iso);
        setSaved(true); setTimeout(() => setSaved(false), 1500);
        setScheduled(false); setActiveFrom("");
    };
    const minLocal = (() => {
        const d = new Date(Date.now() + 60_000);
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16);
    })();
    const openHistory = async () => {
        setShowHistory((v) => !v);
        if (!history) {
            try {
                const r = await api.get(`/admin/payin/wallet/${partnerId}/commissions/${row.mid_id}/history`);
                setHistory(r.data.items || []);
            } catch { setHistory([]); }
        }
    };
    return (
        <>
        <tr className="border-t border-slate-100" data-testid={`com-row-${row.mid_id}`}>
            <td className="px-3 py-2">
                <div className="font-semibold text-xs">{row.mid_label}</div>
                <div className="text-[10px] text-slate-400 font-mono">{row.mid_id}</div>
            </td>
            <td className="px-3 py-2 text-slate-600 uppercase text-[10px]">{row.vendor}</td>
            <td className="px-3 py-2 text-right">
                <input data-testid={`com-pct-${row.mid_id}`} value={pct} onChange={(e) => setPct(e.target.value.replace(/[^0-9.]/g, ""))}
                           className="input text-xs text-right w-20 py-1" inputMode="decimal" />
            </td>
            <td className="px-3 py-2 text-right">
                <input data-testid={`com-flat-${row.mid_id}`} value={flat} onChange={(e) => setFlat(e.target.value.replace(/[^0-9.]/g, ""))}
                           className="input text-xs text-right w-20 py-1" inputMode="decimal" />
            </td>
            <td className="px-3 py-2 text-right">
                <label className="inline-flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                    <input type="checkbox" data-testid={`com-schedule-chk-${row.mid_id}`}
                               checked={scheduled} onChange={(e) => setScheduled(e.target.checked)} />
                    Schedule
                </label>
                <button data-testid={`com-history-${row.mid_id}`} onClick={openHistory}
                            className="ml-2 text-[10px] text-slate-500 hover:text-indigo-700 underline">History</button>
                <button data-testid={`com-save-${row.mid_id}`} onClick={save} disabled={!dirty || busy}
                            className={`ml-2 px-2.5 py-1 rounded text-[10px] font-bold ${dirty ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-slate-100 text-slate-400"}`}>
                    {saved ? "Saved ✓" : (scheduled && activeFrom ? "Schedule" : "Save")}
                </button>
            </td>
        </tr>
        {showHistory && (
            <tr className="bg-slate-50" data-testid={`com-history-panel-${row.mid_id}`}>
                <td colSpan={5} className="px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Rate change history</div>
                    {history === null ? (
                        <div className="text-xs text-slate-400 italic">Loading…</div>
                    ) : history.length === 0 ? (
                        <div className="text-xs text-slate-400 italic">No changes yet.</div>
                    ) : (
                        <ol className="relative border-l border-slate-200 ml-1 space-y-1.5">
                            {history.map((h, i) => (
                                <li key={i} className="pl-3 py-0.5">
                                    <span className={`absolute -left-[5px] w-2.5 h-2.5 rounded-full border-2 border-white ${
                                        h.action === "applied" ? "bg-emerald-500" :
                                        h.action === "scheduled" ? "bg-amber-500" :
                                        h.action === "promoted" ? "bg-indigo-500" : "bg-rose-500"}`} />
                                    <div className="flex flex-wrap gap-1.5 items-baseline text-[11px]">
                                        <span className={`uppercase font-bold text-[9px] px-1.5 py-0.5 rounded ${
                                            h.action === "applied" ? "bg-emerald-100 text-emerald-800" :
                                            h.action === "scheduled" ? "bg-amber-100 text-amber-800" :
                                            h.action === "promoted" ? "bg-indigo-100 text-indigo-800" : "bg-rose-100 text-rose-800"}`}>
                                            {h.action}
                                        </span>
                                        <span className="font-mono font-semibold text-slate-700">
                                            {h.from_percent}% → <span className="text-slate-900">{h.to_percent}%</span>
                                        </span>
                                        {(h.from_flat_fee || h.to_flat_fee) ? (
                                            <span className="font-mono text-slate-500">
                                                · flat ₹{h.from_flat_fee} → ₹{h.to_flat_fee}
                                            </span>
                                        ) : null}
                                        <span className="text-slate-500">by <strong>{h.actor}</strong></span>
                                        {h.active_from && (
                                            <span className="text-amber-700 text-[10px]">from {new Date(h.active_from).toLocaleString()}</span>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                                        {new Date(h.created_at).toLocaleString()}
                                        {h.note && <span className="text-slate-500 italic ml-1.5">· {h.note}</span>}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}
                </td>
            </tr>
        )}
        {scheduled && (
            <tr className="bg-indigo-50/50" data-testid={`com-schedule-row-${row.mid_id}`}>
                <td colSpan={5} className="px-3 py-2">
                    <label className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wider">Activate at</label>
                    <input type="datetime-local" min={minLocal} value={activeFrom}
                                data-testid={`com-active-from-${row.mid_id}`}
                                onChange={(e) => setActiveFrom(e.target.value)}
                                className="input text-xs ml-2 py-1" />
                    <span className="ml-2 text-[10px] text-indigo-700">
                        New rate stays pending until this moment, then takes effect automatically.
                    </span>
                </td>
            </tr>
        )}
        {row.scheduled && !scheduled && (
            <tr className="bg-amber-50/60" data-testid={`com-pending-${row.mid_id}`}>
                <td colSpan={5} className="px-3 py-2 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">⏳ Pending change</span>
                    <span className="text-xs text-slate-700">
                        → <strong>{row.scheduled.percent}%</strong>
                        {row.scheduled.flat_fee > 0 && <> + <strong>₹{row.scheduled.flat_fee}</strong></>}
                        {" "}from <strong>{new Date(row.scheduled.active_from).toLocaleString()}</strong>
                        {row.scheduled.scheduled_by && <span className="text-slate-500"> · set by {row.scheduled.scheduled_by}</span>}
                    </span>
                    <button data-testid={`com-cancel-sched-${row.mid_id}`} onClick={() => onCancelSched(row.mid_id)}
                                className="ml-auto text-[11px] text-rose-700 hover:text-rose-900 font-semibold">Cancel</button>
                </td>
            </tr>
        )}
        </>
    );
}

const EMPTY_MID = { vendor: "payspher", label: "", api_base_url: "", auth_key: "", secret_key: "", notification_url: "", return_url: "", dedicated_partner_id: "", default_flow: "direct" };

function MidsTab() {
    const [mids, setMids] = useState([]);
    const [form, setForm] = useState(EMPTY_MID);
    const [editing, setEditing] = useState(null);  // null | 'new' | mid_id
    const [testing, setTesting] = useState(null);
    const [statRange, setStatRange] = useState("today");
    const [stats] = useMidStats("/admin/payin/mids/stats", statRange);
    const statsById = Object.fromEntries((stats?.items || []).map((s) => [s.mid_id, s]));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [partners, setPartners] = useState([]);
    const pName = (pid) => partners.find((p) => p.partner_id === pid)?.business_name || pid;

    const refresh = async () => {
        try { const r = await api.get("/admin/payin/mids"); setMids(r.data.items || []); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { refresh(); api.get("/admin/payin/wallets").then((r) => setPartners(r.data.items || [])).catch(() => {}); }, []);

    const save = async () => {
        setBusy(true); setErr("");
        try {
            const { dedicated_partner_id, ...rest } = form;
            if (editing === "new") {
                const r = await api.post("/admin/payin/mids", rest);
                if (dedicated_partner_id) await api.patch(`/admin/payin/mids/${r.data.mid.mid_id}`, { dedicated_partner_id });
            } else await api.patch(`/admin/payin/mids/${editing}`, { ...rest, dedicated_partner_id: dedicated_partner_id || "" });
            setEditing(null); setForm(EMPTY_MID); refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    const del = async (m) => {
        if (!window.confirm(`Revoke MID "${m.label}"? Active orders won't verify but historical data is preserved.`)) return;
        try { await api.delete(`/admin/payin/mids/${m.mid_id}`); refresh(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    return (
        <>
            {err && <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700 mb-3">{err}</div>}
            <PayinHealthBar onRan={refresh} />
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <RangeSwitch value={statRange} onChange={setStatRange} testid="payin-mids-range" />
                <button data-testid="payin-mid-new" onClick={() => { setEditing("new"); setForm(EMPTY_MID); }}
                        className="btn-primary px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> New MID
                </button>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl">
                {mids.length === 0 ? (
                    <div className="p-10 text-center text-xs text-slate-500">No payin MIDs yet.</div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {mids.map((m) => (
                            <li key={m.mid_id} className="p-3 flex items-center justify-between" data-testid={`payin-mid-${m.mid_id}`}>
                                <div className="min-w-0">
                                    <div className="font-semibold text-sm flex flex-wrap items-center gap-1.5">{m.label}
                                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${m.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{m.status}</span>
                                        {m.status === "active" && m.last_connection_test && <LastTestChip t={m.last_connection_test} id={m.mid_id} />}
                                        <span data-testid={`payin-mid-flow-${m.mid_id}`} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.default_flow === "redirect" ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-600"}`}>
                                            flow: {m.default_flow || "direct"}
                                        </span>
                                        {m.dedicated_partner_id && (
                                            <span data-testid={`payin-mid-dedicated-${m.mid_id}`} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700">🔒 {pName(m.dedicated_partner_id)}</span>
                                        )}
                                        {m.status === "active" && !m.notification_url && (
                                            <span data-testid={`payin-mid-no-hook-${m.mid_id}`} title="Payspher requires a notification URL. The server will auto-fill it from its domain, but setting it here is safer."
                                                  className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700">Notification URL missing</span>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-slate-500 font-mono">{m.vendor} · {m.mid_id} · auth {m.auth_key_display}</div>
                                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{m.api_base_url}</div>
                                    {m.status === "active" && <MidStatsStrip s={statsById[m.mid_id]} testid={`payin-mid-stats-${m.mid_id}`} />}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setTesting(m)} data-testid={`payin-mid-test-${m.mid_id}`}
                                            className="px-2 py-1 text-[11px] font-semibold text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 rounded flex items-center gap-1 transition-colors">
                                        <Activity className="w-3.5 h-3.5" /> Test
                                    </button>
                                    <button onClick={() => { setEditing(m.mid_id); setForm({ ...EMPTY_MID, ...m, secret_key: "", auth_key: "" }); }}
                                            className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded" data-testid={`payin-mid-edit-${m.mid_id}`}>
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    {m.status === "active" && (
                                        <button onClick={() => del(m)}
                                                className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded" data-testid={`payin-mid-del-${m.mid_id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {editing && (
                <Modal>
                    <div className="bg-white rounded-xl w-full max-w-lg p-5 space-y-3" data-testid="payin-mid-modal">
                        <h3 className="font-bold text-base">{editing === "new" ? "New payin MID" : `Edit MID ${editing}`}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Vendor"><select value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="input w-full text-sm"><option value="payspher">Payspher</option></select></Field>
                            <Field label="Label *"><input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input w-full text-sm" /></Field>
                            <Field label="API base URL *" wide><input value={form.api_base_url} onChange={(e) => setForm({ ...form, api_base_url: e.target.value })} placeholder="https://api.payspher.com/v3/gateway" className="input w-full text-sm font-mono" /></Field>
                            <Field label={editing === "new" ? "Auth key (UUID) *" : "Auth key — leave blank to keep current"}><input value={form.auth_key} onChange={(e) => setForm({ ...form, auth_key: e.target.value })} className="input w-full text-sm font-mono" /></Field>
                            <Field label={editing === "new" ? "Secret key *" : "Secret key — leave blank to keep"}><input type="password" value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })} className="input w-full text-sm font-mono" /></Field>
                            <Field label="Notification URL (webhook) *" wide>
                                <input value={form.notification_url || ""} onChange={(e) => setForm({ ...form, notification_url: e.target.value })} placeholder={DEFAULT_HOOK} data-testid="payin-mid-notification-url" className="input w-full text-sm font-mono" />
                                {!form.notification_url && (
                                    <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-amber-700" data-testid="payin-mid-hook-warning">
                                        <span>Required by Payspher — orders fail with "notification url is required" if empty.</span>
                                        <button type="button" onClick={() => setForm({ ...form, notification_url: DEFAULT_HOOK })} data-testid="payin-mid-hook-default"
                                                className="shrink-0 font-semibold text-indigo-600 hover:text-indigo-800">Use default</button>
                                    </div>
                                )}
                            </Field>
                            <Field label="Dedicated to partner" wide>
                                <select value={form.dedicated_partner_id || ""} onChange={(e) => setForm({ ...form, dedicated_partner_id: e.target.value })}
                                        data-testid="payin-mid-dedicated" className="input w-full text-sm">
                                    <option value="">Not dedicated — can be assigned to any partner</option>
                                    {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.business_name} ({p.partner_id})</option>)}
                                </select>
                            </Field>
                            <Field label="Return URL (after payment)" wide><input value={form.return_url || ""} onChange={(e) => setForm({ ...form, return_url: e.target.value })} className="input w-full text-sm font-mono" /></Field>
                            <Field label="Default flow" wide>
                                <select value={form.default_flow || "direct"} onChange={(e) => setForm({ ...form, default_flow: e.target.value })}
                                        data-testid="payin-mid-default-flow" className="input w-full text-sm">
                                    <option value="direct">Direct — method fixed by us (HPP / QR / UPI)</option>
                                    <option value="redirect">Redirect — customer picks method on Payspher cashier page</option>
                                </select>
                                <div className="mt-1 text-[11px] text-slate-500">Used when neither the order nor the partner's PayIn defaults set a flow.</div>
                            </Field>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-slate-200 text-sm hover:bg-slate-50">Cancel</button>
                            <button onClick={save} disabled={busy} data-testid="payin-mid-save"
                                    className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2">
                                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
            {testing && <PayinConnectionTest mid={testing} onClose={() => { setTesting(null); refresh(); }} />}
        </>
    );
}

const CHIP = { reachable: ["bg-emerald-50 text-emerald-700", "Healthy"], ip_blocked: ["bg-rose-50 text-rose-700", "IP blocked"], unreachable: ["bg-amber-50 text-amber-700", "Unreachable"], misconfigured: ["bg-orange-50 text-orange-700", "Misconfigured"] };

function LastTestChip({ t, id }) {
    const [cls, label] = CHIP[t.verdict] || CHIP.unreachable;
    return (
        <span data-testid={`payin-mid-last-test-${id}`} title={`Checked ${new Date(t.tested_at).toLocaleString()} (${t.source || "manual"}) from ${t.egress_ip}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current" />{label} · {timeAgo(t.tested_at)}
        </span>
    );
}

const DEFAULT_HOOK = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/payspher`;

function Field({ label, children, wide }) {
    return <div className={wide ? "sm:col-span-2" : ""}>
        <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
        <div className="mt-1">{children}</div>
    </div>;
}

const ADMIN_PATHS = {
    list: "/admin/payin/orders/history",
    csv: "/admin/payin/orders/history/export.csv",
    pdf: "/admin/payin/orders/history/export.pdf",
    verify: (id) => `/admin/payin/orders/${id}/verify`,
    receipt: (id) => `/admin/payin/orders/${id}/receipt.pdf`,
    bulkVerify: "/admin/payin/orders/verify-bulk",
    callbacks: (id) => `/admin/payin/orders/${id}/callbacks`,
    resend: (id) => `/admin/payin/orders/${id}/callbacks/resend`,
    fullLog: (id) => `/admin/payin/orders/${id}/vendor-log?full=true`,
};

function OrdersTab() {
    const [partners, setPartners] = useState([]);
    useEffect(() => { api.get("/admin/payin/wallets").then((r) => setPartners(r.data.items || [])).catch(() => {}); }, []);
    return <PayinHistory paths={ADMIN_PATHS} isAdmin partners={partners} testid="admin-ph" />;
}
